import {AnnotationTool, UserCursor} from "@app/review/models";
import {Point, Rect} from "@shared/euclid";
import {getGuid} from "@shared/guid";

// This is here to remove most of the time from Date.now() since it is being cast to a float
export const DATE_OFFSET = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDay()).getTime();

export class RemoteStroke {
    readonly id: string;
    userId: string;
    segments: number[][];
    color: string;
    size: number;
    tool: AnnotationTool;
    startTime: number;
    frame: number;

    constructor(id: string = null) {
        this.id = id || getGuid();
        this.startTime = Date.now() - DATE_OFFSET;
    }
}

export class RemoteStrokeHistory {
    private _remoteStrokes: RemoteStroke[];
    private _remoteStrokeMap: Map<string, RemoteStroke>;
    private _localSegments: Map<string, number[][]>;

    public historyPointer: number = -1;

    get Strokes(): RemoteStroke[] {
        return this._remoteStrokes.filter(i => i.tool !== AnnotationTool.Cursor);
    }

    get Cursors(): RemoteStroke[] {
        return this._remoteStrokes.filter(i => i.tool === AnnotationTool.Cursor);
    }

    constructor() {
        this._remoteStrokes = [];
        this._remoteStrokeMap = new Map<string, RemoteStroke>();
        this._localSegments = new Map<string, number[][]>();
    }

    public strokesForFrame(frame: number): RemoteStroke[] {
        return this.Strokes.filter(i => i.frame === frame);
    }

    public localSegments(id: string) {
        return this._localSegments.get(id);
    }

    private generateMap(): void {
        this._remoteStrokeMap.clear();
        for (const remoteStroke of this._remoteStrokes) {
            this._remoteStrokeMap.set(remoteStroke.id, remoteStroke);
        }
    }

    public getOrCreateStroke(userCursor: UserCursor): RemoteStroke {
        let remoteStroke = this._remoteStrokeMap.get(userCursor.Id);
        if (!remoteStroke) {
            // Create one
            remoteStroke = new RemoteStroke(userCursor.Id);
            remoteStroke.userId = userCursor.UserId;
            remoteStroke.segments = [];
            remoteStroke.color = userCursor.Color;
            remoteStroke.size = userCursor.Size;
            remoteStroke.startTime = userCursor.Time;
            remoteStroke.tool = userCursor.Tool;
            remoteStroke.frame = userCursor.Frame;

            if (remoteStroke.tool !== AnnotationTool.Cursor) {
                this.truncateStrokes(this.historyPointer);
                this.historyPointer += 1;
            }

            this._remoteStrokes.push(remoteStroke);
            this.generateMap();
        }

        return remoteStroke;
    }

    public addStroke(remoteStroke: RemoteStroke) {
        this._remoteStrokes.push(remoteStroke);
        this.generateMap();
    }

    public updateStroke(id: string) {

    }

    public deleteStroke(id: string) {
        const remoteStroke = this._remoteStrokeMap.get(id);
        if (remoteStroke) {
            const index = this._remoteStrokes.indexOf(remoteStroke);
            this._remoteStrokes.splice(index, 1);

            this.generateMap();
        }
    }

    public truncateStrokes(index: number) {
        const strokes = this.Strokes;
        const toRemove = strokes.slice(index + 1);
        for (const remoteStroke of toRemove) {
            const index = this._remoteStrokes.indexOf(remoteStroke);
            this._remoteStrokes.splice(index, 1);
        }

        this.generateMap();
    }

    public clear(): void {
        this.historyPointer = -1;
        this._remoteStrokes = [];
        this._localSegments.clear();
        this.generateMap();
    }

    public computeLocalPoints(assetBounds: Rect): void {
        if (!assetBounds)
            return;

        this._localSegments.clear();
        let localStroke = [];

        for (const remoteStroke of this._remoteStrokes) {
            localStroke = [];
            if (remoteStroke.segments.length === 0) {
                continue;
            }

            for (let i = 0; i < remoteStroke.segments.length; i++) {
                const segment = remoteStroke.segments[i];
                const point = new Point(segment[0], segment[1]);
                const localPoint = assetBounds.fromViewportSpace(point);
                localStroke.push([localPoint.x, localPoint.y, segment[2], segment[3]]);
            }
            this._localSegments.set(remoteStroke.id, localStroke);
        }
    }
}
